import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useChatStore } from '../stores/chatStore';
import { useClipboardStore } from '../stores/clipboardStore';
import { Conversation } from '../types/chat';
import { ClipboardItem } from '../types/clipboard';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { config } from '../config';
import DOMPurify from 'dompurify';

interface TrashViewProps {
  onSwitchToChat?: () => void;
}

interface DeletedConversation extends Conversation {
  deleted_at: string;
}

interface DeletedClipboardItem extends ClipboardItem {
  deleted_at: string;
}

export default function TrashView({ onSwitchToChat }: TrashViewProps) {
  const [deletedConversations, setDeletedConversations] = useState<DeletedConversation[]>([]);
  const [deletedClipboardItems, setDeletedClipboardItems] = useState<DeletedClipboardItem[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingClipboard, setIsLoadingClipboard] = useState(true);
  const [previewItem, setPreviewItem] = useState<DeletedClipboardItem | null>(null);
  const [previewConversation, setPreviewConversation] = useState<DeletedConversation | null>(null);
  const { loadConversations } = useChatStore();
  const { loadItems } = useClipboardStore();

  useEffect(() => {
    loadDeletedConversations();
    loadDeletedClipboardItems();
  }, []);

  const loadDeletedConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/conversations/deleted`, {});

      if (!response.ok) {
        throw new Error('Failed to load deleted conversations');
      }

      const data = await response.json();
      setDeletedConversations(data);
    } catch (error) {
      console.error('Error loading deleted conversations:', error);
      toast.error('Failed to load deleted conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const loadDeletedClipboardItems = async () => {
    setIsLoadingClipboard(true);
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/clipboard/items/deleted`, {});

      if (!response.ok) {
        throw new Error('Failed to load deleted clipboard items');
      }

      const data = await response.json();
      setDeletedClipboardItems(data.items || []);
    } catch (error) {
      console.error('Error loading deleted clipboard items:', error);
      toast.error('Failed to load deleted clipboard items');
    } finally {
      setIsLoadingClipboard(false);
    }
  };

  const restoreConversation = async (conversationId: string) => {
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/conversations/${conversationId}/restore`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to restore conversation');
      }

      toast.success('Conversation restored successfully');
      await loadDeletedConversations();
      await loadConversations(); // Refresh the main conversation list
    } catch (error) {
      console.error('Error restoring conversation:', error);
      toast.error('Failed to restore conversation');
    }
  };

  const permanentlyDeleteConversation = async (conversationId: string) => {
    if (!confirm('Are you sure? This will permanently delete this conversation and cannot be undone.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/conversations/${conversationId}/permanent`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to permanently delete conversation');
      }

      toast.success('Conversation permanently deleted');
      await loadDeletedConversations();
    } catch (error) {
      console.error('Error permanently deleting conversation:', error);
      toast.error('Failed to permanently delete conversation');
    }
  };

  const restoreClipboardItem = async (itemId: string) => {
    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/clipboard/items/${itemId}/restore`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to restore clipboard item');
      }

      toast.success('Clipboard item restored successfully');
      await loadDeletedClipboardItems();
      await loadItems(); // Refresh the main clipboard list
    } catch (error) {
      console.error('Error restoring clipboard item:', error);
      toast.error('Failed to restore clipboard item');
    }
  };

  const permanentlyDeleteClipboardItem = async (itemId: string) => {
    if (!confirm('Are you sure? This will permanently delete this clipboard item and cannot be undone.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${config.apiBaseUrl}/api/clipboard/items/${itemId}/permanent`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to permanently delete clipboard item');
      }

      toast.success('Clipboard item permanently deleted');
      await loadDeletedClipboardItems();
    } catch (error) {
      console.error('Error permanently deleting clipboard item:', error);
      toast.error('Failed to permanently delete clipboard item');
    }
  };

  const getDaysUntilPurge = (deletedAt: string): number => {
    const deletedDate = new Date(deletedAt);
    const purgeDate = new Date(deletedDate);
    purgeDate.setDate(purgeDate.getDate() + 7);
    const now = new Date();
    const daysRemaining = Math.ceil((purgeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewItem(null);
        setPreviewConversation(null);
      }
    };

    if (previewItem || previewConversation) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [previewItem, previewConversation]);

  const closeModals = () => {
    setPreviewItem(null);
    setPreviewConversation(null);
  };

  const handleRestoreFromModal = async () => {
    if (previewConversation) {
      await restoreConversation(previewConversation.id);
      closeModals();
    } else if (previewItem) {
      await restoreClipboardItem(previewItem.id);
      closeModals();
    }
  };

  const handlePermanentDeleteFromModal = async () => {
    if (previewConversation) {
      await permanentlyDeleteConversation(previewConversation.id);
      closeModals();
    } else if (previewItem) {
      await permanentlyDeleteClipboardItem(previewItem.id);
      closeModals();
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-dark-border bg-dark-sidebar p-4">
        <div className="flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-dark-muted">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
          <div>
            <h1 className="text-xl font-semibold text-dark-text">Trash</h1>
            <p className="text-sm text-dark-muted">Items will be permanently deleted after 7 days</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        {/* Deleted Conversations Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-text">Deleted Conversations</h2>
            {deletedConversations.length > 0 && (
              <span className="text-sm text-dark-muted">
                {deletedConversations.length} {deletedConversations.length === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>

          {isLoadingConversations ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : deletedConversations.length === 0 ? (
            <div className="text-center py-8 text-dark-muted">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mx-auto mb-3 opacity-50">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
              </svg>
              <p>No deleted conversations</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {deletedConversations.map((conversation) => {
                const daysRemaining = getDaysUntilPurge(conversation.deleted_at);
                return (
                  <div
                    key={conversation.id}
                    className="bg-dark-card border border-dark-border rounded-lg p-4 hover:border-accent-blue/50 transition-colors cursor-pointer"
                    onClick={() => setPreviewConversation(conversation)}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-dark-text font-medium truncate mb-1">
                          {conversation.title}
                        </h3>
                        <p className="text-sm text-dark-muted">
                          Deleted {formatDate(conversation.deleted_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        daysRemaining <= 2
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-yellow-500/10 text-yellow-400'
                      }`}>
                        {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} until permanent deletion
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreConversation(conversation.id);
                        }}
                        className="flex-1 px-3 py-2 bg-accent-blue text-white text-sm rounded hover:bg-accent-blue/80 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                        </svg>
                        Restore
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          permanentlyDeleteConversation(conversation.id);
                        }}
                        className="px-3 py-2 bg-red-500/10 text-red-400 text-sm rounded hover:bg-red-500/20 transition-colors flex items-center justify-center"
                        title="Delete permanently"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Deleted Clipboard Items Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-text">Deleted Clipboard Items</h2>
            {deletedClipboardItems.length > 0 && (
              <span className="text-sm text-dark-muted">
                {deletedClipboardItems.length} {deletedClipboardItems.length === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>

          {isLoadingClipboard ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-accent-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : deletedClipboardItems.length === 0 ? (
            <div className="text-center py-8 text-dark-muted">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mx-auto mb-3 opacity-50">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
              </svg>
              <p>No deleted clipboard items</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {deletedClipboardItems.map((item) => {
                const daysRemaining = getDaysUntilPurge(item.deleted_at);
                const previewLength = 100;
                const content = item.content_variants?.plain || item.title || 'No content';
                const preview = content.length > previewLength
                  ? content.substring(0, previewLength) + '...'
                  : content;

                return (
                  <div
                    key={item.id}
                    className="bg-dark-card border border-dark-border rounded-lg p-4 hover:border-accent-blue/50 transition-colors cursor-pointer"
                    onClick={() => setPreviewItem(item)}
                  >
                    <div className="mb-3">
                      <div className="text-sm text-dark-text line-clamp-3 mb-2 font-mono bg-dark-bg p-2 rounded">
                        {preview}
                      </div>
                      <p className="text-xs text-dark-muted">
                        Deleted {formatDate(item.deleted_at)}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        daysRemaining <= 2
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-yellow-500/10 text-yellow-400'
                      }`}>
                        {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} until permanent deletion
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreClipboardItem(item.id);
                        }}
                        className="flex-1 px-3 py-2 bg-accent-blue text-white text-sm rounded hover:bg-accent-blue/80 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                        </svg>
                        Restore
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          permanentlyDeleteClipboardItem(item.id);
                        }}
                        className="px-3 py-2 bg-red-500/10 text-red-400 text-sm rounded hover:bg-red-500/20 transition-colors flex items-center justify-center"
                        title="Delete permanently"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Conversation Preview Modal */}
      {previewConversation && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeModals}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-lg max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] flex flex-col text-sm sm:text-base"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex-1 min-w-0 pr-2">
                <h2 className="text-lg sm:text-xl font-semibold text-dark-text mb-2 truncate">
                  {previewConversation.title}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-dark-muted">
                  <span>Deleted {formatDate(previewConversation.deleted_at)}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    getDaysUntilPurge(previewConversation.deleted_at) <= 2
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {getDaysUntilPurge(previewConversation.deleted_at)} days left
                  </span>
                </div>
              </div>
              <button
                onClick={closeModals}
                className="text-dark-muted hover:text-dark-text active:text-dark-text transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
              {previewConversation.messages && previewConversation.messages.length > 0 ? (
                previewConversation.messages.map((message, index) => (
                  <div
                    key={index}
                    className={`p-3 sm:p-4 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-accent-blue/10 border border-accent-blue/30'
                        : 'bg-dark-bg border border-dark-border'
                    }`}
                  >
                    <div className="text-xs font-semibold text-dark-muted mb-2 uppercase">
                      {message.role === 'user' ? 'You' : 'Assistant'}
                    </div>
                    <div className="text-dark-text whitespace-pre-wrap text-sm sm:text-base">
                      {message.content}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-dark-muted py-8">
                  No messages in this conversation
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 lg:p-6 border-t border-dark-border">
              <button
                onClick={closeModals}
                className="sm:hidden px-4 py-3 bg-dark-border text-dark-text rounded min-h-[44px] hover:bg-dark-bg active:bg-dark-bg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handlePermanentDeleteFromModal}
                className="px-4 py-3 sm:py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 active:bg-red-500/30 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-sm sm:text-base">Delete Permanently</span>
              </button>
              <button
                onClick={handleRestoreFromModal}
                className="px-4 py-3 sm:py-2 bg-accent-blue text-white rounded hover:bg-accent-blue/80 active:bg-accent-blue/70 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                <span className="text-sm sm:text-base">Restore</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clipboard Item Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeModals}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-lg max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] flex flex-col text-sm sm:text-base"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex-1 min-w-0 pr-2">
                <h2 className="text-lg sm:text-xl font-semibold text-dark-text mb-2 truncate">
                  {previewItem.title || 'Clipboard Item'}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-dark-muted">
                  <span>Deleted {formatDate(previewItem.deleted_at)}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    getDaysUntilPurge(previewItem.deleted_at) <= 2
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  }`}>
                    {getDaysUntilPurge(previewItem.deleted_at)} days left
                  </span>
                </div>
              </div>
              <button
                onClick={closeModals}
                className="text-dark-muted hover:text-dark-text active:text-dark-text transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
              {/* Check for image content */}
              {previewItem.content_variants?.image && (
                <div className="mb-4">
                  <img
                    src={previewItem.content_variants.image}
                    alt={previewItem.title || 'Clipboard image'}
                    className="max-w-full h-auto rounded-lg border border-dark-border"
                  />
                </div>
              )}

              {/* Display HTML content if available */}
              {previewItem.content_variants?.html && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">HTML Content</div>
                  <div
                    className="bg-dark-bg border border-dark-border rounded-lg p-4 prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewItem.content_variants.html) }}
                  />
                </div>
              )}

              {/* Display plain text content */}
              {previewItem.content_variants?.plain && (
                <div className="mb-4">
                  {previewItem.content_variants.html || previewItem.content_variants.image ? (
                    <div className="text-sm font-semibold text-dark-muted mb-2">Plain Text</div>
                  ) : null}
                  <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                    <pre className="text-dark-text whitespace-pre-wrap font-mono text-sm">
                      {previewItem.content_variants.plain}
                    </pre>
                  </div>
                </div>
              )}

              {/* If no content variants, show title or fallback */}
              {!previewItem.content_variants?.plain && !previewItem.content_variants?.html && !previewItem.content_variants?.image && (
                <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                  <p className="text-dark-muted text-sm italic">
                    {previewItem.title || 'No content available'}
                  </p>
                </div>
              )}

              {/* Display all available content variants */}
              {previewItem.content_variants && Object.keys(previewItem.content_variants).length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">
                    Available Formats: {Object.keys(previewItem.content_variants).join(', ')}
                  </div>
                </div>
              )}

              {previewItem.tags && previewItem.tags.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {previewItem.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-accent-blue/10 text-accent-blue text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 lg:p-6 border-t border-dark-border">
              <button
                onClick={closeModals}
                className="sm:hidden px-4 py-3 bg-dark-border text-dark-text rounded min-h-[44px] hover:bg-dark-bg active:bg-dark-bg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handlePermanentDeleteFromModal}
                className="px-4 py-3 sm:py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 active:bg-red-500/30 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-sm sm:text-base">Delete Permanently</span>
              </button>
              <button
                onClick={handleRestoreFromModal}
                className="px-4 py-3 sm:py-2 bg-accent-blue text-white rounded hover:bg-accent-blue/80 active:bg-accent-blue/70 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                <span className="text-sm sm:text-base">Restore</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
